import {
  AccessTimeRounded,
  CheckCircleOutlineRounded,
  ChevronRightRounded,
  ClearRounded,
  DescriptionOutlined,
  SearchRounded,
  StorageRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MEMORY_TABS } from 'src/constants/memory';
import { PATHS } from 'src/constants/navigateRoutes';
import { ROLES } from 'src/constants/Role';
import { getReviewedActions } from 'src/services/actionReview';
import { getActivity } from 'src/services/invocation-history';
import { listSourceCatalog } from 'src/services/sourceOperations';
import { listWorkspaceNotes } from 'src/services/workspaceNotes';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';

const SEARCH_DELAY_MS = 300;
const RESULT_LIMIT = 8;
const CATEGORY_LIMIT = 4;

type SearchGroup = 'Actions' | 'Sources' | 'Notes' | 'Activity';

interface SearchResult {
  id: string;
  group: SearchGroup;
  title: string;
  description: string;
  meta: string;
  href: string;
  icon: typeof SearchRounded;
}

const GROUPS: SearchGroup[] = ['Actions', 'Sources', 'Notes', 'Activity'];

const interleaveResults = (results: SearchResult[]) => {
  const grouped = new Map(
    GROUPS.map((group) => [
      group,
      results.filter((result) => result.group === group),
    ]),
  );
  const interleaved: SearchResult[] = [];

  for (let index = 0; interleaved.length < RESULT_LIMIT; index += 1) {
    let added = false;

    GROUPS.forEach((group) => {
      const result = grouped.get(group)?.[index];

      if (result && interleaved.length < RESULT_LIMIT) {
        interleaved.push(result);
        added = true;
      }
    });

    if (!added) break;
  }

  return interleaved;
};

const summarize = (value: string | null | undefined, fallback: string) => {
  const normalized = value?.replace(/\s+/g, ' ').trim();

  if (!normalized) return fallback;

  return normalized.length > 150
    ? `${normalized.slice(0, 147).trimEnd()}…`
    : normalized;
};

const quickResults: SearchResult[] = [
  {
    id: 'quick-actions',
    group: 'Actions',
    title: 'What needs attention?',
    description: 'Review open, overdue, and suggested Actions.',
    meta: 'Open Actions',
    href: `${PATHS.tasks}?view=open&due=overdue`,
    icon: WarningAmberRounded,
  },
  {
    id: 'quick-sources',
    group: 'Sources',
    title: 'Source health',
    description: 'Review connected evidence, access, and monitoring health.',
    meta: 'Workspace Sources',
    href: `${PATHS.memory}?tab=${MEMORY_TABS.sourceHealth}`,
    icon: StorageRounded,
  },
  {
    id: 'quick-notes',
    group: 'Notes',
    title: 'Workspace notes',
    description: 'Open shared policies and grounded workspace context.',
    meta: 'Workspace Notes',
    href: `${PATHS.memory}?tab=${MEMORY_TABS.workspaceNotes}`,
    icon: DescriptionOutlined,
  },
  {
    id: 'quick-activity',
    group: 'Activity',
    title: 'Recent Hana activity',
    description: 'Review your latest questions and Hana responses.',
    meta: 'My Hana invocations',
    href: PATHS.invocationHistory,
    icon: AccessTimeRounded,
  },
];

const GlobalSearch = () => {
  const navigate = useNavigate();
  const user = useAuthStore().getUserDetails();
  const { organizationDetails } = useOrganizationStore();
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [failedCategoryCount, setFailedCategoryCount] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const isAdmin =
    user?.role === ROLES.ADMIN ||
    user?.role === ROLES.DOMAIN_OWNER ||
    user?.role === ROLES.SUPER_ADMIN;
  const trackingEnabled = organizationDetails?.allowInvocationHistory !== false;
  const normalizedInput = queryInput.trim();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(queryInput.trim());
    }, SEARCH_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [queryInput]);

  useEffect(() => {
    setQueryInput('');
    setQuery('');
    setResults([]);
    setFailedCategoryCount(0);
    setOpen(false);
    requestIdRef.current += 1;
  }, [user?.organizationId]);

  useEffect(() => {
    const closeFloatingSearch = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeFloatingSearch);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeFloatingSearch);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (query.length < 2) {
      setResults([]);
      setFailedCategoryCount(0);
      setLoading(false);

      return;
    }

    const load = async () => {
      setLoading(true);
      const requests = await Promise.allSettled([
        getReviewedActions({
          page: 1,
          limit: CATEGORY_LIMIT,
          query,
          view: 'all',
          includeCounts: false,
        }),
        listSourceCatalog({ page: 1, limit: CATEGORY_LIMIT, search: query }),
        listWorkspaceNotes({
          page: 1,
          limit: CATEGORY_LIMIT,
          search: query,
        }),
        trackingEnabled
          ? getActivity({
              page: 1,
              limit: CATEGORY_LIMIT,
              scope: isAdmin ? 'workspace' : 'self',
              query,
            })
          : Promise.resolve(null),
      ]);

      if (requestId !== requestIdRef.current) return;

      const [actionsResult, sourcesResult, notesResult, activityResult] =
        requests;
      const nextResults: SearchResult[] = [];
      const searchableOutcomes = trackingEnabled
        ? requests
        : requests.slice(0, 3);

      setFailedCategoryCount(
        searchableOutcomes.filter(
          (result) =>
            result.status === 'rejected' ||
            !result.value ||
            !result.value.success,
        ).length,
      );

      if (
        actionsResult.status === 'fulfilled' &&
        actionsResult.value.success &&
        actionsResult.value.content
      ) {
        nextResults.push(
          ...actionsResult.value.content.data.map((action) => ({
            id: `action-${action.id}`,
            group: 'Actions' as const,
            title: action.title,
            description: summarize(
              action.nextAction ?? action.description,
              'Open the Action record and its evidence.',
            ),
            meta:
              action.kind === 'suggestion'
                ? 'Suggested Action'
                : `${action.state.replaceAll('_', ' ')} · ${action.evidenceCount} evidence link${action.evidenceCount === 1 ? '' : 's'}`,
            href:
              action.kind === 'suggestion' && !action.acceptedTaskId
                ? `${PATHS.tasks}?view=suggestions&suggestion=${encodeURIComponent(action.id)}`
                : `${PATHS.tasks}?id=${encodeURIComponent(action.acceptedTaskId ?? action.id)}`,
            icon:
              action.kind === 'suggestion'
                ? WarningAmberRounded
                : CheckCircleOutlineRounded,
          })),
        );
      }

      if (
        sourcesResult.status === 'fulfilled' &&
        sourcesResult.value.success &&
        sourcesResult.value.content
      ) {
        nextResults.push(
          ...sourcesResult.value.content.data.map((source) => ({
            id: `source-${source.sourceId}`,
            group: 'Sources' as const,
            title: source.title,
            description: summarize(
              source.useCaseInstructions,
              'Open source health, access, and evidence details.',
            ),
            meta: `${source.provider.replaceAll('_', ' ')} · ${source.healthStatus.replaceAll('_', ' ')}`,
            href: `${PATHS.memory}?tab=${MEMORY_TABS.sourceHealth}&sourceId=${encodeURIComponent(source.sourceId)}`,
            icon: StorageRounded,
          })),
        );
      }

      if (
        notesResult.status === 'fulfilled' &&
        notesResult.value.success &&
        notesResult.value.content
      ) {
        nextResults.push(
          ...notesResult.value.content.data.map((note) => ({
            id: `note-${note._id}`,
            group: 'Notes' as const,
            title: note.title,
            description: summarize(note.body, 'Open this workspace note.'),
            meta: `Workspace note · ${note.indexStatus.toLowerCase().replaceAll('_', ' ')}`,
            href: `${PATHS.memory}?tab=${MEMORY_TABS.workspaceNotes}&note=${encodeURIComponent(note._id)}`,
            icon: DescriptionOutlined,
          })),
        );
      }

      if (
        activityResult.status === 'fulfilled' &&
        activityResult.value?.success &&
        activityResult.value.content
      ) {
        const scope = isAdmin ? 'workspace' : 'self';

        nextResults.push(
          ...activityResult.value.content.data.map((activity) => ({
            id: `activity-${activity.id}`,
            group: 'Activity' as const,
            title: summarize(activity.invocation, 'Hana invocation'),
            description: summarize(activity.response, 'Open Hana response.'),
            meta: `${activity.actor.displayName} · ${new Date(activity.createdAt).toLocaleString()}`,
            href: `${PATHS.invocationHistory}?scope=${scope}&query=${encodeURIComponent(query)}&id=${encodeURIComponent(activity.id)}`,
            icon: AccessTimeRounded,
          })),
        );
      }

      setResults(interleaveResults(nextResults));
      setLoading(false);
    };

    void load();

    return () => {
      requestIdRef.current += 1;
    };
  }, [isAdmin, query, trackingEnabled]);

  const visibleResults = normalizedInput ? results : quickResults;
  const groupedResults = useMemo(
    () =>
      GROUPS.map((group) => ({
        group,
        results: visibleResults.filter((result) => result.group === group),
      })).filter((section) => section.results.length > 0),
    [visibleResults],
  );

  const openResult = (result: SearchResult) => {
    requestIdRef.current += 1;
    navigate(result.href);
    setOpen(false);
    setQueryInput('');
    setQuery('');
    setResults([]);
    setFailedCategoryCount(0);
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        display: { xs: 'none', md: 'block' },
        position: 'relative',
        width: 224,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          height: 36,
          px: 1.25,
          borderColor: open ? 'primary.main' : 'divider',
          boxShadow: open
            ? (theme) => `0 0 0 2px ${theme.palette.primary.main}20`
            : 'none',
          bgcolor: 'background.default',
        }}
      >
        <SearchRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
        <InputBase
          value={queryInput}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            const nextInput = event.target.value;
            const nextQuery = nextInput.trim();

            setQueryInput(nextInput);

            if (nextQuery !== query) {
              requestIdRef.current += 1;
              setResults([]);
              setFailedCategoryCount(0);
              setLoading(nextQuery.length >= 2);
            }

            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (visibleResults[0]) openResult(visibleResults[0]);
            else setOpen(true);
          }}
          inputProps={{ 'aria-label': 'Search Hana' }}
          placeholder="Global search…"
          sx={{ minWidth: 0, flex: 1, fontSize: 14 }}
        />
        {loading ? (
          <CircularProgress size={14} />
        ) : queryInput ? (
          <IconButton
            size="small"
            aria-label="Clear search"
            onClick={() => {
              setQueryInput('');
              setQuery('');
              setResults([]);
              setFailedCategoryCount(0);
              requestIdRef.current += 1;
              setLoading(false);
            }}
            sx={{ p: 0.25 }}
          >
            <ClearRounded sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Paper>

      {open && (
        <Paper
          elevation={12}
          role="dialog"
          aria-label="Hana search results"
          sx={{
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.modal,
            top: 'calc(100% + 8px)',
            left: 0,
            width: 420,
            maxHeight: 460,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              bgcolor: 'background.default',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="overline" color="text.secondary">
              {normalizedInput ? 'Search results' : 'Quick search'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {normalizedInput
                ? loading
                  ? 'Searching…'
                  : `${results.length} result${results.length === 1 ? '' : 's'}`
                : 'Enter opens the first result'}
            </Typography>
          </Box>

          {!loading && normalizedInput.length === 1 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" fontWeight={700}>
                Keep typing to search Hana
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Enter at least two characters.
              </Typography>
            </Box>
          )}

          {!loading && failedCategoryCount > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ m: 1.5 }}>
              {results.length > 0
                ? 'Some search categories are temporarily unavailable. These results are partial.'
                : failedCategoryCount === (trackingEnabled ? 4 : 3)
                  ? 'Hana search is temporarily unavailable. Try again shortly.'
                  : 'No matches were found in the available categories. Some results could not be checked.'}
            </Alert>
          )}

          {!loading && normalizedInput.length >= 2 && results.length === 0 && (
            <Box
              sx={{
                px: 2,
                py: failedCategoryCount > 0 ? 2 : 4,
                textAlign: 'center',
              }}
            >
              <SearchRounded
                sx={{ fontSize: 34, color: 'text.secondary', mb: 1 }}
              />
              <Typography variant="body2" fontWeight={700}>
                {failedCategoryCount > 0
                  ? 'No available matches'
                  : 'No matches found'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Try a title, source, policy phrase, or Hana question.
              </Typography>
            </Box>
          )}

          {groupedResults.map((section) => (
            <Box
              component="section"
              aria-label={`${section.group} search results`}
              key={section.group}
            >
              <Typography
                component="div"
                variant="overline"
                color="text.secondary"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: 'action.hover',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {section.group}
              </Typography>
              {section.results.map((result) => {
                const Icon = result.icon;

                return (
                  <Box
                    component="button"
                    type="button"
                    key={result.id}
                    onClick={() => openResult(result)}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      px: 1.5,
                      py: 1.25,
                      border: 0,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      textAlign: 'left',
                      cursor: 'pointer',
                      '&:hover, &:focus-visible': {
                        bgcolor: 'action.hover',
                        outline: 'none',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        bgcolor: 'action.hover',
                        color: 'primary.main',
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {result.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                        }}
                      >
                        {result.description}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="primary.main"
                        display="block"
                        sx={{ mt: 0.25 }}
                      >
                        {result.meta}
                      </Typography>
                    </Box>
                    <ChevronRightRounded
                      sx={{ mt: 0.5, fontSize: 18, color: 'text.secondary' }}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;
